import { useState, useEffect, useRef, useMemo } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Clock, MapPin, Phone, Mail, LogOut, CalendarIcon, Navigation, MessageCircle, Info, Camera, Archive, X, CreditCard, User, AlertTriangle, FileCheck, Edit, Shield, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Link, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { CreateJobDialog } from "./CreateJobDialog";
import { JobTrackingDialog } from "./JobTrackingDialog";
import { TradesmanSelectionDialog } from "./TradesmanSelectionDialog";
import { SimpleJobResponsesDialog } from "./SimpleJobResponsesDialog";
import { JobMessagesInbox } from "./JobMessagesInbox";
import { PaymentDialog } from "./PaymentDialog";
import { JobMessagingDialog } from "./JobMessagingDialog";
import { LocationControls } from "./LocationControls";
import JobRatingSection from "./JobRatingSection";
import {
  JobCardShell,
  JobCardHeaderSection,
  JobCardDivider,
  JobCardBody,
  JobCardDescription,
  JobCardInfoRow,
  JobCardInfoSection,
  JobCardActions,
  JobCardActionRow,
  JobCardButton,
  getUnifiedStatusColor,
} from "@/components/job-card/JobCardComponents";
import RatingDisplay from "./RatingDisplay";
import ContactVerificationDialog from "./ContactVerificationDialog";
import { CustomerJobDetailDialog } from "./CustomerJobDetailDialog";
import UserRatingsDialog from "./UserRatingsDialog";
import { JobPhotoManager } from "./JobPhotoManager";
import { JobArchiveDialog } from "@/components/JobArchiveDialog";
import { JobCancelDialog } from "@/components/JobCancelDialog";
import { ArchivedJobsDialog } from "@/components/ArchivedJobsDialog";
import { CustomerReviewsDialog } from "./CustomerReviewsDialog";
import { TradesmenChatDropdown } from "./TradesmenChatDropdown";
import { JobStatusManager } from "./JobStatusManager";
import { ModernLogo } from "./ModernLogo";
import { ContactInfoDialog } from "./ContactInfoDialog";
import { LiveChatWidget } from "./LiveChatWidget";
import { FloatingAppDownload } from "./FloatingAppDownload";
import { EmailVerificationDialog } from "./EmailVerificationDialog";
import { ReportJobIssueDialog } from "./ReportJobIssueDialog";
import { LegalDocumentDownloader } from "./LegalDocumentDownloader";
import { PasswordResetDialog } from "./PasswordResetDialog";
import { EditJobDialog } from "./EditJobDialog";
import dashboardHero from "@/assets/dashboard-hero.jpg";
import customerSuccess from "@/assets/customer-success.jpg";

// Safe date formatting helper to avoid crashes on invalid dates
const safeFormat = (dateInput: any, fmt: string, fallback = "N/A") => {
  const d = new Date(dateInput);
  return !isNaN(d.getTime()) ? format(d, fmt) : fallback;
};
interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  priority: string;
  status: string;
  budget_min?: number;
  budget_max?: number;
  required_date?: string;
  response_by: string;
  created_at: string;
  updated_at: string;
  customer_id: string;
  tradesman_id: string | null;
  job_category?: string;
  agreed_cost?: number;
  response_count?: number;
  message_count?: number;
  unread_message_count?: number;
  total_activity_count?: number;
  job_number: number;
  required_specialties?: string[];
}
interface CustomerDashboardProps {
  onSignOut: () => void;
  focusedJobId?: string | null;
  chatAction?: string | null;
  chatWithUserId?: string | null;
  showPasswordReset?: boolean;
  onPasswordResetChange?: (open: boolean) => void;
}
export const CustomerDashboard = ({
  onSignOut,
  focusedJobId,
  chatAction,
  chatWithUserId,
  showPasswordReset = false,
  onPasswordResetChange,
}: CustomerDashboardProps) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [trackingJob, setTrackingJob] = useState<Job | null>(null);
  const [selectingTradesmanForJob, setSelectingTradesmanForJob] = useState<Job | null>(null);
  const [viewingResponsesForJob, setViewingResponsesForJob] = useState<string>("");
  const [jobMessagesInboxOpen, setJobMessagesInboxOpen] = useState<string>("");
  const [openedJobMessageIds, setOpenedJobMessageIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('openedJobMessageIds');
      return raw ? new Set<string>(JSON.parse(raw)) : new Set<string>();
    } catch { return new Set<string>(); }
  });
  const [messagingJob, setMessagingJob] = useState<Job | null>(null);
  const [messagingRecipientId, setMessagingRecipientId] = useState<string | undefined>(undefined);
  const [paymentJob, setPaymentJob] = useState<Job | null>(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [managingPhotosJobId, setManagingPhotosJobId] = useState<string>("");
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedJobForAction, setSelectedJobForAction] = useState<Job | null>(null);
  const [archivedJobIds, setArchivedJobIds] = useState<string[]>([]);
  const [mutuallyRatedJobIds, setMutuallyRatedJobIds] = useState<Set<string>>(new Set());
  const [cancellations, setCancellations] = useState<any[]>([]);
  const [showArchivedJobs, setShowArchivedJobs] = useState(false);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showUserRatingsDialog, setShowUserRatingsDialog] = useState(false);
  const [customerJobDetailDialog, setCustomerJobDetailDialog] = useState<Job | null>(null);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [completedPaymentJobIds, setCompletedPaymentJobIds] = useState<string[]>([]);
  const [viewedQuoteJobIds, setViewedQuoteJobIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const [viewedQuotesLoaded, setViewedQuotesLoaded] = useState(false);
  const [reportingJob, setReportingJob] = useState<Job | null>(null);
  const [showEmailVerificationDialog, setShowEmailVerificationDialog] = useState(false);
  const [jobPhotos, setJobPhotos] = useState<Record<string, string[]>>({});
  const [showReviewsDialog, setShowReviewsDialog] = useState(false);
   const [showLocationControls, setShowLocationControls] = useState(true);
   const [activeTab, setActiveTab] = useState('posted');
   const restoredRecentlyRef = useRef<Map<string, number>>(new Map());
   const [forceShowJobIds, setForceShowJobIds] = useState<Set<string>>(new Set());
   const [pinnedCompletedJobIds, setPinnedCompletedJobIds] = useState<Set<string>>(new Set());
   const [pinnedCompletedJobsData, setPinnedCompletedJobsData] = useState<Record<string, Job>>({});
  
  // Check if there are unseen quotes
  const hasUnseenQuotes = viewedQuotesLoaded && jobs.some(job => 
    (job.status === 'quoted' || job.status === 'quote_accepted' || job.status === 'payment_pending') && 
    !viewedQuoteJobIds.includes(job.id)
  );

  // Calculate job counts for tab badges
  const jobCounts = useMemo(() => {
    return {
      posted: jobs.filter(job => job.status === 'pending').length,
      quoted: jobs.filter(job => (job.status === 'quoted' || job.status === 'quote_accepted' || job.status === 'payment_pending')).length,
      inProgress: jobs.filter(job => (job.status === 'assigned' || job.status === 'in_progress')).length,
      completed: jobs.filter(job => job.status === 'completed' && (!archivedJobIds.includes(job.id) || forceShowJobIds.has(job.id) || pinnedCompletedJobIds.has(job.id))).length
    };
  }, [jobs, archivedJobIds, forceShowJobIds]);

  const isMobile = useIsMobile();
  const getUnread = (jobId: string) => {
    // While the inbox/dialog for this job is open, always show 0 and no blinking
    if (jobMessagesInboxOpen === jobId || messagingJob?.id === jobId) return 0;
    if (openedJobMessageIds.has(jobId)) return 0;
    const j = jobs.find((x) => x.id === jobId);
    return j?.unread_message_count || 0;
  };
  const {
    toast
  } = useToast();
  const chatInitRef = useRef(false);
  const currentUserProfileRef = useRef<any>(null);
  useEffect(() => {
    currentUserProfileRef.current = currentUserProfile;
  }, [currentUserProfile]);

  // Persist suppression set
  useEffect(() => {
    try { localStorage.setItem('openedJobMessageIds', JSON.stringify(Array.from(openedJobMessageIds))); } catch {}
  }, [openedJobMessageIds]);

  useEffect(() => {
    const initializeAndCheckPendingJobs = async () => {
      await fetchJobs();

      // Check for pending job data from localStorage after user is authenticated
      const pendingJobData = localStorage.getItem('pendingJobData');
      if (pendingJobData) {
        try {
          const jobData = JSON.parse(pendingJobData);
          localStorage.removeItem('pendingJobData');
          setShowCreateJob(true);
        } catch (error) {
          console.error('Error parsing pending job data:', error);
          localStorage.removeItem('pendingJobData');
        }
      }
    };
    initializeAndCheckPendingJobs();
  }, []);

  // Persist viewed quotes per user in localStorage
  useEffect(() => {
    if (currentUserProfile?.id) {
      const key = `viewedQuoteJobIds:${currentUserProfile.id}`;
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            setViewedQuoteJobIds(parsed);
          }
        }
      } catch {}
      // Mark as loaded after attempting to read
      setViewedQuotesLoaded(true);
    }
  }, [currentUserProfile?.id]);

  useEffect(() => {
    if (currentUserProfile?.id) {
      const key = `viewedQuoteJobIds:${currentUserProfile.id}`;
      try {
        localStorage.setItem(key, JSON.stringify(viewedQuoteJobIds));
      } catch {}
    }
  }, [viewedQuoteJobIds, currentUserProfile?.id]);

  // Load pinned completed jobs from localStorage when profile is available
  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        const raw = localStorage.getItem(`pinnedCompletedJobs:${currentUserProfile.id}`);
        setPinnedCompletedJobIds(raw ? new Set<string>(JSON.parse(raw)) : new Set<string>());
      } catch {}
    }
  }, [currentUserProfile?.id]);

  // Persist pinned completed jobs
  useEffect(() => {
    if (currentUserProfile?.id) {
      try {
        localStorage.setItem(`pinnedCompletedJobs:${currentUserProfile.id}`, JSON.stringify(Array.from(pinnedCompletedJobIds)));
      } catch {}
    }
  }, [pinnedCompletedJobIds, currentUserProfile?.id]);

  // Handle auto-opening chat from email links
  useEffect(() => {
    console.log('CustomerDashboard: Chat action effect triggered', {
      chatAction,
      focusedJobId,
      chatWithUserId,
      jobsLength: jobs.length,
      chatInitRef: chatInitRef.current
    });
    
    if (chatAction === 'chat' && focusedJobId && chatWithUserId && jobs.length > 0 && !chatInitRef.current) {
      const job = jobs.find(j => j.id === focusedJobId);
      console.log('CustomerDashboard: Found job for chat action:', job);
      
      if (job) {
        console.log('Auto-opening chat from email link:', {
          job: job.id,
          with: chatWithUserId
        });
        setMessagingJob(job);
        setMessagingRecipientId(chatWithUserId);
        chatInitRef.current = true;

        // Clear URL parameters to prevent re-opening and clear stored intent
        try { localStorage.removeItem('emailChatIntent'); } catch {}
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        console.log('CustomerDashboard: Job not found for chat action, available jobs:', jobs.map(j => ({ id: j.id, title: j.title })));
      }
    }
  }, [chatAction, focusedJobId, chatWithUserId, jobs]);

  // Realtime subscription to profile updates and message updates
  useEffect(() => {
    let profileChannel: any = null;
    let messageChannel: any = null;
    let archiveChannel: any = null;
    let isCancelled = false;
    (async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user || isCancelled) return;
      console.log('CustomerDashboard: Setting up realtime subscriptions for user:', user.id);
      
      // Profile updates subscription
      profileChannel = supabase.channel(`customer-profile-updates-${user.id}-${crypto.randomUUID()}`).on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `user_id=eq.${user.id}`
      }, payload => {
        console.log('CustomerDashboard: Received profile update:', payload.new);
        setCurrentUserProfile((prev: any) => {
          const updated = {
            ...prev,
            ...payload.new
          };
          console.log('CustomerDashboard: Updated profile state:', updated);
          return updated;
        });
      }).subscribe(status => {
        console.log('CustomerDashboard: Profile subscription status:', status);
      });

      // Messages subscription - refresh jobs when messages are read, and clear suppression on new messages
      messageChannel = supabase.channel(`customer-message-updates-${user.id}-${crypto.randomUUID()}`)
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
        }, payload => {
          console.log('CustomerDashboard: Received message update:', payload.new);
          // Refresh jobs to update unread counts when message read status changes
          if (payload.new.is_read_by_recipient !== payload.old?.is_read_by_recipient) {
            console.log('CustomerDashboard: Message read status changed, refreshing jobs');
            fetchJobs();
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        }, payload => {
          const newMsg = payload.new as any;
          // If a brand-new message arrives for this customer and a job we suppressed, unsuppress it
          const profileId = currentUserProfileRef.current?.id;
          if (profileId && newMsg.recipient_id === profileId) {
            setOpenedJobMessageIds(prev => {
              if (!prev.has(newMsg.job_id)) return prev;
              const next = new Set(prev);
              next.delete(newMsg.job_id);
              return next;
            });
          }
          fetchJobs();
        })

        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'job_responses'
        }, payload => {
          console.log('CustomerDashboard: New job response received:', payload.new);
          // Refresh jobs when new responses are added (quotes received)
          fetchJobs();
        })
        .subscribe(status => {
          console.log('CustomerDashboard: Messages subscription status:', status);
        });

      // Subscribe to archive changes to keep lists in sync
      archiveChannel = supabase
        .channel(`job-archives-${user.id}-${crypto.randomUUID()}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'job_user_archives' }, (payload) => {
          const affectedProfileId = (payload.new as any)?.profile_id || (payload.old as any)?.profile_id;
          if (affectedProfileId && currentUserProfile?.id === affectedProfileId) {
            console.log('CustomerDashboard: Archive change detected for current user, refreshing');
            fetchArchivedJobIds();
            fetchJobs();
          }
        })
        .subscribe((status) => {
          console.log('CustomerDashboard: Archive subscription status:', status);
        });

    })();
    return () => {
      isCancelled = true;
      if (profileChannel) {
        console.log('CustomerDashboard: Cleaning up profile subscription');
        supabase.removeChannel(profileChannel);
      }
      if (messageChannel) {
        console.log('CustomerDashboard: Cleaning up message subscription');
        supabase.removeChannel(messageChannel);
      }
      if (archiveChannel) {
        console.log('CustomerDashboard: Cleaning up archive subscription');
        supabase.removeChannel(archiveChannel);
      }
    };
  }, []);
  useEffect(() => {
    if (currentUserProfile?.id) {
      fetchArchivedJobIds();
    }
  }, [currentUserProfile]);
  useEffect(() => {
    if (jobs.length > 0) {
      fetchCancellations();
    }
  }, [jobs]);

  // Ensure suppression when a full messaging dialog opens directly
  useEffect(() => {
    if (messagingJob?.id) {
      const jobId = messagingJob.id;
      setOpenedJobMessageIds(prev => { const n = new Set(prev); n.add(jobId); return n; });
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, unread_message_count: 0 } : j));
    }
  }, [messagingJob?.id]);

  // Handle focused job from email links
  useEffect(() => {
    if (focusedJobId && jobs.length > 0) {
      const focusedJob = jobs.find(job => job.id === focusedJobId);
      if (focusedJob) {
        // Auto-open the job detail dialog for the focused job
        setCustomerJobDetailDialog(focusedJob);
        toast({
          title: "Quote Details",
          description: "Showing details for the job from your email link."
        });
      }
    }
  }, [focusedJobId, jobs]);
  const handleArchiveJob = (job: Job) => {
    setSelectedJobForAction(job);
    setIsArchiveDialogOpen(true);
  };
  const handleCancelJob = (job: Job) => {
    setSelectedJobForAction(job);
    setIsCancelDialogOpen(true);
  };
  const handleArchiveSuccess = () => {
    if (selectedJobForAction?.id) {
      setPinnedCompletedJobIds(prev => {
        const next = new Set(prev);
        next.delete(selectedJobForAction.id);
        return next;
      });
    }
    fetchArchivedJobIds();
  };
  const handleCancelSuccess = () => {
    fetchJobs();
    fetchCancellations();
  };
  const fetchArchivedJobIds = async () => {
    if (!currentUserProfile?.id) return;
    try {
      const { data: archived } = await supabase.rpc('get_archived_jobs_for_current_user');
      const nowTs = Date.now();
      for (const [id, ts] of restoredRecentlyRef.current.entries()) {
        if (nowTs - ts > 15000) restoredRecentlyRef.current.delete(id);
      }
      const ids = (archived || []).map((a: any) => a.job_id).filter((id: string) => !restoredRecentlyRef.current.has(id));
      setArchivedJobIds(ids);
    } catch (error) {
      console.error("Error fetching archived jobs:", error);
    }
  };
  const fetchCancellations = async () => {
    if (!jobs.length) return;
    try {
      const {
        data: cancellationsData
      } = await supabase.from("job_cancellations").select("*").in("job_id", jobs.map(j => j.id));
      setCancellations(cancellationsData || []);
    } catch (error) {
      console.error("Error fetching cancellations:", error);
    }
  };
  const createJobFromPendingData = async (jobData: any) => {
    try {
      if (!currentUserProfile?.id) {
        // If profile not loaded yet, get it first
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        const {
          data: profile
        } = await supabase.from("profiles").select("id").eq("user_id", user.id).maybeSingle();
        if (!profile) throw new Error("Profile not found");

        // Use this profile id for job creation
        await createJobWithProfileId(jobData, profile.id);
      } else {
        await createJobWithProfileId(jobData, currentUserProfile.id);
      }
    } catch (error) {
      console.error("Failed to create job from pending data:", error);
      toast({
        title: "Error creating job",
        description: "There was an issue creating your job. Please try again.",
        variant: "destructive"
      });
    }
  };
  const createJobWithProfileId = async (jobData: any, profileId: string) => {
    // Combine date and time for required_date
    let requiredDate: string | null = null;
    if (jobData.preferredDate && jobData.preferredTime) {
      const [hours, minutes] = jobData.preferredTime.split(':');
      const combinedDate = new Date(jobData.preferredDate);
      combinedDate.setHours(parseInt(hours), parseInt(minutes));
      requiredDate = combinedDate.toISOString();
    }

    // Create the job
    const responseBy = new Date();
    responseBy.setDate(responseBy.getDate() + 7);
    const {
      data: newJobData,
      error
    } = await supabase.from("jobs").insert({
      customer_id: profileId,
      title: jobData.title,
      description: jobData.description,
      location: jobData.location,
      priority: jobData.priority,
      job_category: jobData.jobCategory,
      budget_min: jobData.budgetMin ? parseFloat(jobData.budgetMin) : null,
      budget_max: jobData.budgetMax ? parseFloat(jobData.budgetMax) : null,
      required_date: requiredDate,
      response_by: responseBy.toISOString()
    }).select().maybeSingle();
    if (error) throw error;
    if (!newJobData) throw new Error("Failed to create job");
    toast({
      title: "Job created successfully!",
      description: "Your job request has been posted and qualified tradesmen have been notified."
    });

    // Refresh jobs list
    fetchJobs();
  };

  const handleVerificationUpdate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && currentUserProfile) {
        const { data: latest } = await supabase
          .from("profiles")
          .select("email_verified, phone_verified")
          .eq("user_id", user.id)
          .maybeSingle();

        if (latest) {
          setCurrentUserProfile({ ...currentUserProfile, ...latest });
        }
      }
    } catch (error) {
      console.error("Error updating verification status:", error);
    }
  };
  const fetchJobs = async () => {
    try {
      // First get the customer's profile
      const {
        data: profile
      } = await supabase.from("profiles").select("id, full_name, user_type, average_rating, total_reviews, email, phone, email_verified, phone_verified, address, company_name, created_at, updated_at").eq("user_id", (await supabase.auth.getUser()).data.user?.id).maybeSingle();
      if (!profile) return;
      setCurrentUserProfile(profile);

      // Check admin status
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', authUser.id)
          .eq('role', 'admin')
          .maybeSingle();
        setIsAdmin(!!roleData);
      }

      // Check if new customer needs email verification
      if (!profile.email_verified && profile.user_type === 'customer') {
        // Check if this is a new customer (less than 1 hour since registration)
        const profileCreatedAt = new Date(profile.created_at);
        const hoursSinceCreation = (Date.now() - profileCreatedAt.getTime()) / (1000 * 60 * 60);
        
        if (hoursSinceCreation < 24) { // Show for customers registered within last 24 hours
          setTimeout(() => {
            setShowEmailVerificationDialog(true);
          }, 1000); // Small delay to let the dashboard load first
        }
      }

      // Prefetch archived job IDs for this customer to filter at source
      const { data: archivedRows } = await supabase.rpc('get_archived_jobs_for_current_user');
      let prefetchedArchivedJobIds = (archivedRows || []).map((a: any) => a.job_id);
      // Ignore recently restored IDs briefly to prevent flicker
      const nowTs = Date.now();
      for (const [id, ts] of restoredRecentlyRef.current.entries()) {
        if (nowTs - ts > 15000) restoredRecentlyRef.current.delete(id);
      }
      prefetchedArchivedJobIds = prefetchedArchivedJobIds.filter((id: string) => !restoredRecentlyRef.current.has(id));
      setArchivedJobIds(prefetchedArchivedJobIds);

      // Then fetch their jobs with response counts and message counts
      const { data: jobsData, error } = await supabase
        .from("jobs")
        .select(`
          *,
          job_responses(count)
        `)
        .eq("customer_id", profile.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Format the data to include response_count and message_count
      const formattedJobs = await Promise.all((jobsData || []).map(async (job) => {
        try {
          // Get message counts for this job
          const { data: allMessagesData, error: allMessagesError } = await supabase
            .from("messages")
            .select("id, is_read_by_recipient, recipient_id")
            .eq("job_id", job.id)
            .eq("is_blocked", false);
          
          // Get unread message count where customer is the recipient
          const { data: unreadMessagesData, error: unreadMessagesError } = await supabase
            .from("messages")
            .select("id")
            .eq("job_id", job.id)
            .eq("recipient_id", profile.id)
            .eq("is_read_by_recipient", false)
            .eq("is_blocked", false);
          
          console.log(`Messages for job ${job.id}:`, allMessagesData, allMessagesError);
          console.log(`Unread messages for job ${job.id}:`, unreadMessagesData, unreadMessagesError);
          
          // Simple message count
          const responseCount = job.job_responses[0]?.count || 0;
          const messageCount = allMessagesData?.length || 0;
          const unreadMessageCount = unreadMessagesData?.length || 0;
          const totalActivityCount = responseCount + messageCount;
          
          console.log(`Job ${job.id} counts:`, { responseCount, messageCount, unreadMessageCount, totalActivityCount });

          // Determine if job should move to quoted status
          let adjustedStatus = job.status;
          if (job.status === 'pending' && responseCount > 0) {
            adjustedStatus = 'quoted';
          }

          return {
            ...job,
            status: adjustedStatus,
            response_count: responseCount,
            message_count: messageCount,
            unread_message_count: unreadMessageCount,
            total_activity_count: totalActivityCount
          };
        } catch (error) {
          console.error(`Error processing job ${job.id}:`, error);
          return {
            ...job,
            response_count: job.job_responses[0]?.count || 0,
            message_count: 0,
            unread_message_count: 0,
            total_activity_count: job.job_responses[0]?.count || 0
          };
        }
      })) || [];

      // Use prefetched archived IDs to filter jobs at source
      const archivedJobIds = prefetchedArchivedJobIds;


      // Filter out archived jobs from the main job list
      const nonArchivedJobs = formattedJobs.filter(job => !archivedJobIds.includes(job.id) || forceShowJobIds.has(job.id) || pinnedCompletedJobIds.has(job.id));
      console.log(`Filtered jobs: ${formattedJobs.length} total → ${nonArchivedJobs.length} non-archived`);

       // Fetch payment information for jobs
      let completedPaymentJobIds: string[] = [];
      if (nonArchivedJobs.length > 0) {
        try {
          // Use secure function to get customer payments
          const { data: paymentData } = await supabase.rpc('get_customer_payments');
          completedPaymentJobIds = paymentData?.filter(p => p.status === "completed").map(p => p.job_id) || [];
          setCompletedPaymentJobIds(completedPaymentJobIds);
          console.log('Customer completed payments:', completedPaymentJobIds);
        } catch (paymentError) {
          console.error("Error fetching payment data:", paymentError);
        }

        // Fetch job photos
        try {
          const { data: photosData, error: photoError } = await supabase
            .from("job_photos")
            .select("job_id, file_path")
            .in("job_id", nonArchivedJobs.map(j => j.id));
          
          console.log('Fetched job photos:', photosData, photoError);
          
          const photosMap: Record<string, string[]> = {};
          photosData?.forEach(photo => {
            if (!photosMap[photo.job_id]) {
              photosMap[photo.job_id] = [];
            }
            const photoUrl = supabase.storage.from("job-photos").getPublicUrl(photo.file_path).data.publicUrl;
            console.log(`Photo URL for job ${photo.job_id}:`, photoUrl);
            photosMap[photo.job_id].push(photoUrl);
          });
          setJobPhotos(photosMap);
          console.log('Job photos map:', photosMap);
        } catch (photoError) {
          console.error("Error fetching job photos:", photoError);
        }
      }

      // Force-hide completed jobs that have mutual reviews (in case archives lag)
      try {
        const completedIds = nonArchivedJobs.filter(j => j.status === 'completed').map(j => j.id);
        if (completedIds.length > 0) {
          const { data: ratingRows } = await supabase
            .from('ratings')
            .select('job_id')
            .in('job_id', completedIds);
          const counts = new Map<string, number>();
          (ratingRows || []).forEach(r => counts.set(r.job_id, (counts.get(r.job_id) || 0) + 1));
          const twoPlus = new Set(Array.from(counts.entries()).filter(([, c]) => c >= 2).map(([jobId]) => jobId));
          setMutuallyRatedJobIds(twoPlus);
        } else {
          setMutuallyRatedJobIds(new Set());
        }
      } catch (e) {
        console.warn('Mutual rating check failed, proceeding without force-hide', e);
      }

       setJobs(nonArchivedJobs);
       
       // Debug: Check for duplicate jobs
       const jobIds = nonArchivedJobs.map(j => j.id);
       const duplicateIds = jobIds.filter((id, index) => jobIds.indexOf(id) !== index);
       if (duplicateIds.length > 0) {
         console.warn("Duplicate job IDs found:", duplicateIds);
         console.log("Jobs causing duplicates:", nonArchivedJobs.filter(j => duplicateIds.includes(j.id)));
       }
    } catch (error: any) {
      toast({
        title: "Error fetching jobs",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    onSignOut();
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "quote_accepted":
        return "bg-blue-100 text-blue-800";
      case "payment_pending":
        return "bg-purple-100 text-purple-800";
      case "assigned":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-orange-100 text-orange-800";
      case "completed":
        return "bg-emerald-100 text-emerald-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "emergency":
        return "text-red-600";
      case "urgent":
        return "text-orange-600";
      case "standard":
        return "text-blue-600";
      default:
        return "text-gray-600";
    }
  };
  const formatBudget = (min?: number, max?: number) => {
    if (!min && !max) return "Budget not specified";
    if (min && max) return `£${min} - £${max}`;
    if (min) return `From £${min}`;
    if (max) return `Up to £${max}`;
    return "";
  };
  return <div className="min-h-screen bg-gradient-subtle">
      {/* Compact Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/40 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col py-2">
            {/* Mobile: Optimized header layout */}
            {isMobile ? <>
                {/* Top row: Brand + actions */}
                <div className="flex items-center justify-between py-2">
                  <ModernLogo size="sm" showTagline={false} />

                  <div className="flex items-center gap-0.5">
                    {isAdmin && (
                      <Button
                        onClick={() => navigate('/admin')}
                        variant="ghost"
                        size="sm"
                        aria-label="Admin"
                        className="h-9 w-9 p-0 rounded-full text-primary hover:bg-primary/10"
                      >
                        <Shield className="h-5 w-5" />
                      </Button>
                    )}
                    <Button asChild variant="ghost" size="sm" aria-label="About" className="h-9 w-9 p-0 rounded-full text-muted-foreground hover:bg-muted">
                      <Link to="/about">
                        <Info className="h-5 w-5" />
                      </Link>
                    </Button>
                    <Button onClick={handleSignOut} variant="ghost" size="sm" aria-label="Sign out" className="h-9 w-9 p-0 rounded-full text-destructive hover:bg-destructive/10 ml-0.5">
                      <LogOut className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Page title + greeting */}
                <div className="border-t border-border/40 pt-2 pb-2.5">
                  <h1 className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-0.5">
                    Dashboard
                  </h1>
                  {currentUserProfile && (
                    <p className="text-sm text-muted-foreground font-medium leading-tight">
                      Welcome, <span className="text-foreground font-semibold">{currentUserProfile.full_name || 'Customer'}</span>
                    </p>
                  )}
                </div>


                {/* Second row: Rating and verification status */}
                {currentUserProfile && <div className="flex items-center justify-between">
                    <div className="bg-accent/10 px-3 py-1.5 rounded-full border border-accent/20">
                      <RatingDisplay rating={currentUserProfile.average_rating} totalReviews={currentUserProfile.total_reviews} size="sm" onClick={() => setShowUserRatingsDialog(true)} />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 cursor-pointer" onClick={() => setShowEmailVerificationDialog(true)}>
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <Badge variant={currentUserProfile.email_verified ? "default" : "destructive"} className="text-xs px-1.5 py-0.5 h-5">
                          {currentUserProfile.email_verified ? "✓" : "✗"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        <Badge variant={currentUserProfile.phone_verified ? "default" : "destructive"} className="text-xs px-1.5 py-0.5 h-5">
                          {currentUserProfile.phone_verified ? "✓" : "✗"}
                        </Badge>
                      </div>
                    </div>
                  </div>}

                {/* Verification button for mobile */}
                {(!currentUserProfile?.email_verified || !currentUserProfile?.phone_verified) && <Button variant="outline" size="sm" onClick={() => setShowVerificationDialog(true)} className="w-full h-9 border-accent-orange/30 text-accent-orange">
                    Complete Verification
                  </Button>}
              </> : <>
                {/* Desktop layout */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <ModernLogo size="sm" showTagline={false} />
                    <div className="flex items-center gap-3">
                      <h1 className="text-xl font-semibold text-foreground">
                        Customer Dashboard
                      </h1>
                      
                      {currentUserProfile && <div className="flex items-center gap-3 pl-3 border-l border-border/30">
                          <span className="text-sm font-medium text-muted-foreground">
                            Welcome, {currentUserProfile.full_name || 'Customer'}
                          </span>
                          
                          <div className="bg-accent/10 px-3 py-1.5 rounded-full border border-accent/20">
                            <RatingDisplay rating={currentUserProfile.average_rating} totalReviews={currentUserProfile.total_reviews} size="sm" onClick={() => setShowUserRatingsDialog(true)} />
                          </div>
                        </div>}
                      
                      {/* Verification Status */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 cursor-pointer" onClick={() => setShowEmailVerificationDialog(true)}>
                          <Mail className="h-3 w-3" />
                          <Badge variant={currentUserProfile?.email_verified ? "default" : "destructive"} className="text-xs px-2 py-0.5">
                            {currentUserProfile?.email_verified ? "✓" : "✗"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <Badge variant={currentUserProfile?.phone_verified ? "default" : "destructive"} className="text-xs px-2 py-0.5">
                            {currentUserProfile?.phone_verified ? "✓" : "✗"}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {(!currentUserProfile?.email_verified || !currentUserProfile?.phone_verified) && <Button variant="outline" size="sm" onClick={() => setShowVerificationDialog(true)} className="text-xs h-7 px-2 border-accent-orange/30">
                        Verify
                      </Button>}
                    
                    {isAdmin && (
                      <Button 
                        onClick={() => navigate('/admin')} 
                        variant="default" 
                        size="sm"
                        className="gap-2 h-7 px-2"
                      >
                        <Shield className="h-3 w-3" />
                        Admin
                      </Button>
                    )}
                    
                    <Button asChild variant="ghost" size="sm" className="hover:bg-accent/10">
                      <Link to="/about">
                        <Info className="h-4 w-4 mr-1" />
                        About
                      </Link>
                    </Button>
                    <Button onClick={handleSignOut} variant="outline" size="sm" className="h-7 px-2 border-destructive/30">
                      <LogOut className="h-3 w-3 mr-1" />
                      <span className="hidden md:inline text-xs">Sign Out</span>
                    </Button>
                  </div>
                </div>
              </>}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-hero">
        <div className="absolute inset-0">
          <img src={dashboardHero} alt="Professional construction team" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-primary/60 mix-blend-multiply" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="text-center">
            <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl sm:text-4xl lg:text-5xl'} font-bold text-white mb-4`}>
              Welcome Back{currentUserProfile?.full_name ? `, ${currentUserProfile.full_name.split(' ')[0]}` : ''}
            </h1>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-white/90 mb-6 sm:mb-8 max-w-2xl mx-auto`}>
              Track your maintenance requests and enjoy our fast tradesman response guarantee
            </p>
            <div className={`flex ${isMobile ? 'flex-col gap-3' : 'flex-col sm:flex-row gap-4'} justify-center`}>
              <Button 
                onClick={() => {
                  if (!currentUserProfile?.email_verified) {
                    setShowEmailVerificationDialog(true);
                    return;
                  }
                  navigate('/create-job');
                }}
                variant="secondary" 
                size="lg" 
                className="shadow-glow hover:shadow-elegant transition-all duration-300"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Job Request
              </Button>
              <Button onClick={() => setShowArchivedJobs(true)} variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:shadow-md transition-all duration-200">
                <Archive className="h-5 w-5 mr-2" />
                Archived Jobs
              </Button>
              {currentUserProfile && currentUserProfile.total_reviews > 0 && (
                <Button onClick={() => setShowReviewsDialog(true)} variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:shadow-md transition-all duration-200">
                  <Star className="h-5 w-5 mr-2" />
                  My Reviews ({currentUserProfile.total_reviews})
                </Button>
              )}
              <Button onClick={() => setShowContactInfo(true)} variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:shadow-md transition-all duration-200">
                <User className="h-5 w-5 mr-2" />
                My Profile
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Beautiful separator */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gradient-subtle"></div>
        </div>
        
      </div>

      {/* Main Content with Tabs */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isMobile ? 'py-4' : 'py-6 sm:py-8'}`}>
        {isLoading ? <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading your jobs...</p>
          </div> : jobs.length === 0 ? <div className="relative">
            <Card className="text-center py-16 shadow-elegant hover:shadow-glow transition-all duration-300 border-0 bg-gradient-subtle">
              <CardContent>
                <div className="relative">
                  <img src={customerSuccess} alt="Customer satisfaction" className="w-32 h-32 object-cover rounded-full mx-auto mb-6 shadow-elegant" />
                  <div className="absolute inset-0 bg-gradient-hero rounded-full w-32 h-32 mx-auto opacity-10"></div>
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Ready to Get Started?</h3>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto text-lg">
                  Create your first job request and experience our fast response guarantee from qualified tradesmen
                </p>
                <Button 
                  onClick={() => {
                    if (!currentUserProfile?.email_verified) {
                      setShowEmailVerificationDialog(true);
                      return;
                    }
                    navigate('/create-job');
                  }}
                  variant="hero" 
                  size="lg" 
                  className="shadow-glow hover:shadow-elegant transition-all duration-300"
                >
                  Create Your First Job
                </Button>
              </CardContent>
            </Card>
          </div> : <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="relative mb-8">
              {/* Gradient overlays to indicate scrollability */}
              <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10" />
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10" />
              
              <div className="overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory touch-pan-x">
                <TabsList className="inline-flex w-auto min-w-full p-1 gap-2 bg-muted/50">
                  <TabsTrigger 
                    value="posted" 
                    className="flex items-center gap-2 whitespace-nowrap px-6 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start"
                  >
                    Posted
                    {jobCounts.posted > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {jobCounts.posted}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="quoted" 
                    className={`flex items-center gap-2 whitespace-nowrap px-6 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start ${hasUnseenQuotes ? 'animate-pulse bg-yellow-100 border border-yellow-400' : ''}`}
                  >
                    <span>Quoted</span>
                    {hasUnseenQuotes && <span className="text-yellow-600">●</span>}
                    {jobCounts.quoted > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {jobCounts.quoted}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="in-progress" 
                    className="flex items-center gap-2 whitespace-nowrap px-6 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start"
                  >
                    In Progress
                    {jobCounts.inProgress > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {jobCounts.inProgress}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="completed" 
                    className="flex items-center gap-2 whitespace-nowrap px-6 py-3 data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-300 snap-start"
                  >
                    Completed
                    {jobCounts.completed > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {jobCounts.completed}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>
            </div>

            {/* Posted Jobs Tab */}
            <TabsContent value="posted">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-2">Posted Jobs</h2>
                <p className="text-muted-foreground">Jobs waiting for tradesman responses</p>
              </div>
              
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {jobs.filter(job => job.status === 'pending').map(job => {
              const cancellation = cancellations.find(c => c.job_id === job.id);
              const isCancelled = !!cancellation;
              return <JobCardShell key={job.id} status={isCancelled ? 'cancelled' : job.status} className={isCancelled ? 'opacity-60' : ''}>
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={job.location}
                      status={isCancelled ? 'cancelled' : job.status}
                      statusLabel={isCancelled ? 'Cancelled' : undefined}
                      priority={job.priority}
                      jobCategory={job.job_category}
                      jobNumber={job.job_number}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        <JobCardInfoRow label="Budget" value={formatBudget(job.budget_min, job.budget_max)} variant="success" />
                        {job.required_date && <JobCardInfoRow label="Required" value={safeFormat(job.required_date, "MMM d, yyyy 'at' h:mm a", "Not picked")} />}
                        <JobCardInfoRow label="Responses by" value={safeFormat(job.response_by, "MMM d, yyyy")} variant="accent" />
                        <JobCardInfoRow label="Responses" value={`${job.response_count || 0}`} variant="muted" />
                      </JobCardInfoSection>
                      <JobCardActions>
                        <Button
                          onClick={() => { setJobMessagesInboxOpen(job.id); setOpenedJobMessageIds(prev => { const n = new Set(prev); n.add(job.id); return n; }); setJobs(prev => prev.map(j => j.id === job.id ? { ...j, unread_message_count: 0 } : j)); }}
                          variant="outline" size="sm" disabled={isCancelled}
                          className={`w-full shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                            getUnread(job.id) > 0 ? 'border-destructive bg-destructive/10 text-destructive animate-pulse' : ''
                          }`}
                        >
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Job Messages ({getUnread(job.id)})
                        </Button>
                        <JobCardButton onClick={() => setCustomerJobDetailDialog(job)} icon={<Info className="h-4 w-4" />} label="Job Details" />
                        <JobCardButton onClick={() => setEditingJob(job)} icon={<Edit className="h-4 w-4" />} label="Edit Job" />
                        <JobCardButton onClick={() => setManagingPhotosJobId(job.id)} icon={<Camera className="h-4 w-4" />} label="Manage Photos" />
                        {!isCancelled && <JobCardButton onClick={() => handleCancelJob(job)} icon={<X className="h-4 w-4" />} label="Cancel" variant="destructive" />}
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>;
            })}
              </div>
            </TabsContent>

            {/* Quoted Jobs Tab */}
            <TabsContent value="quoted">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-2">Quoted Jobs</h2>
                <p className="text-muted-foreground">Jobs with tradesman quotes awaiting your decision</p>
              </div>
              
               <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                 {jobs
                   .filter(job => (job.status === 'quoted' || job.status === 'quote_accepted' || job.status === 'payment_pending'))
                   .filter((job, index, array) => array.findIndex(j => j.id === job.id) === index) // Remove duplicates
                   .map((job, index) => {
                   console.log(`Quoted job ${index}:`, { id: job.id, title: job.title, status: job.status });
                   const hasNewQuotes = viewedQuotesLoaded && job.status === 'quoted' && !viewedQuoteJobIds.includes(job.id);
                   return <JobCardShell key={job.id} status={job.status} highlight={hasNewQuotes}>
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={job.location}
                      status={job.status}
                      statusLabel={job.status === 'quote_accepted' ? 'Payment Required' : job.status === 'payment_pending' ? 'Payment Pending' : 'Quoted'}
                      priority={job.priority}
                      jobCategory={job.job_category}
                      jobNumber={job.job_number}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        <JobCardInfoRow label="Budget" value={formatBudget(job.budget_min, job.budget_max)} />
                        {job.agreed_cost && <JobCardInfoRow label="Total Cost" value={<span className="text-emerald-600">£{Number(job.agreed_cost).toFixed(2)}</span>} variant="success" />}
                        <JobCardInfoRow label="Required" value={safeFormat(job.required_date, "MMM d, yyyy 'at' h:mm a", "Not picked")} />
                        <JobCardInfoRow label="Responses" value={`${job.response_count || 0}`} variant="muted" />
                      </JobCardInfoSection>
                      <JobCardActions>
                        <Button
                          onClick={() => { setViewedQuoteJobIds(prev => prev.includes(job.id) ? prev : [...prev, job.id]); setViewingResponsesForJob(job.id); }}
                          variant="outline" size="sm"
                          className={`w-full shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                            hasNewQuotes ? 'bg-yellow-100 text-yellow-900 border-yellow-300 hover:bg-yellow-200 animate-pulse font-semibold' : ''
                          }`}
                        >
                          <MessageCircle className="h-4 w-4 mr-1.5" />
                          {hasNewQuotes ? 'New Quotes Available!' : 'View Quotes'}
                        </Button>
                        <Button
                          onClick={() => { setJobMessagesInboxOpen(job.id); setOpenedJobMessageIds(prev => { const n = new Set(prev); n.add(job.id); return n; }); setJobs(prev => prev.map(j => j.id === job.id ? { ...j, unread_message_count: 0 } : j)); }}
                          variant="outline" size="sm"
                          className={`w-full shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                            getUnread(job.id) > 0 ? 'border-destructive bg-destructive/10 text-destructive animate-pulse' : ''
                          }`}
                        >
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Job Messages ({getUnread(job.id)})
                        </Button>
                        <JobCardButton onClick={() => setEditingJob(job)} icon={<Edit className="h-4 w-4" />} label="Edit Job" />
                        {((job.status === 'quote_accepted' || job.status === 'payment_pending') && job.agreed_cost) || (job.status === 'quoted' && job.response_count && job.response_count > 0) ? (
                          <>
                            <JobCardButton
                              onClick={() => {
                                if (job.status === 'quoted') { setViewedQuoteJobIds(prev => prev.includes(job.id) ? prev : [...prev, job.id]); setViewingResponsesForJob(job.id); }
                                else if (job.agreed_cost) { setPaymentJob(job); }
                                else { toast({ title: "Cannot process payment", description: "No agreed cost found for this job", variant: "destructive" }); }
                              }}
                              label={job.status === 'quoted' ? 'Pay and Book Tradesman' : job.status === 'quote_accepted' ? 'Pay and Book Tradesman' : 'View Payment Status'}
                              variant="hero"
                            />
                            <JobCardButton onClick={() => handleCancelJob(job)} icon={<X className="h-4 w-4" />} label="Cancel Job" variant="destructive" />
                          </>
                        ) : null}
                      </JobCardActions>
                    </JobCardBody>
                  </JobCardShell>;
                })}
              </div>
            </TabsContent>


            {/* In Progress Jobs Tab */}
            <TabsContent value="in-progress">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-2">Jobs In Progress</h2>
                <p className="text-muted-foreground">Active jobs being worked on by tradesmen</p>
              </div>
              
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {jobs.filter(job => (job.status === 'assigned' || job.status === 'in_progress')).map(job => (
                  <JobCardShell key={job.id} status={job.status}>
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={job.location}
                      status={job.status}
                      statusLabel={job.status === 'assigned' ? 'Assigned' : 'In Progress'}
                      priority={job.priority}
                      jobCategory={job.job_category}
                      jobNumber={job.job_number}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        {job.agreed_cost && <JobCardInfoRow label="Total Cost" value={<span className="text-emerald-600">£{Number(job.agreed_cost).toFixed(2)}</span>} variant="success" />}
                        {job.required_date && <JobCardInfoRow label="Required" value={safeFormat(job.required_date, "MMM d, yyyy 'at' h:mm a", "Not picked")} />}
                      </JobCardInfoSection>
                      <JobCardActions>
                        <Button
                          onClick={() => { setJobMessagesInboxOpen(job.id); setOpenedJobMessageIds(prev => { const n = new Set(prev); n.add(job.id); return n; }); setJobs(prev => prev.map(j => j.id === job.id ? { ...j, unread_message_count: 0 } : j)); }}
                          variant="outline" size="sm"
                          className={`w-full shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                            getUnread(job.id) > 0 ? 'border-destructive bg-destructive/10 text-destructive animate-pulse' : ''
                          }`}
                        >
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Job Messages ({getUnread(job.id)})
                        </Button>
                        <JobCardButton onClick={() => setEditingJob(job)} icon={<Edit className="h-4 w-4" />} label="Edit Job" />
                        {job.tradesman_id && (
                          <>
                            <JobCardButton onClick={() => setMessagingJob(job)} icon={<MessageCircle className="h-4 w-4" />} label="Direct Messenger" />
                            <JobCardButton onClick={() => setTrackingJob(job)} icon={<Navigation className="h-4 w-4" />} label="Track Progress" />
                          </>
                        )}
                        <JobCardButton onClick={() => setReportingJob(job)} icon={<AlertTriangle className="h-4 w-4" />} label="Report Job Issue" variant="destructive" />
                      </JobCardActions>
                      {/* Job Status Management */}
                      {currentUserProfile && (job.status === 'assigned' || job.status === 'in_progress') && (
                        <div>
                          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mb-3"></div>
                          <JobStatusManager job={job} onJobUpdate={fetchJobs} userType="customer" />
                        </div>
                      )}
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            </TabsContent>

            {/* Completed Jobs Tab */}
            <TabsContent value="completed">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-foreground mb-2">Completed Jobs</h2>
                <p className="text-muted-foreground">Successfully completed jobs</p>
              </div>
              
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
                {jobs.filter(job => job.status === 'completed' && (!archivedJobIds.includes(job.id) || forceShowJobIds.has(job.id) || pinnedCompletedJobIds.has(job.id))).map(job => (
                  <JobCardShell key={job.id} status="completed">
                    <JobCardHeaderSection
                      jobId={job.id}
                      title={job.title}
                      location={job.location}
                      status="completed"
                      statusLabel="✓ Completed"
                      priority={job.priority}
                      jobCategory={job.job_category}
                      jobNumber={job.job_number}
                    />
                    <JobCardDivider />
                    <JobCardBody>
                      <JobCardDescription text={job.description} />
                      <JobCardInfoSection>
                        {job.agreed_cost && <JobCardInfoRow label="Total Paid" value={<span className="text-emerald-600">£{Number(job.agreed_cost).toFixed(2)}</span>} variant="success" />}
                        <JobCardInfoRow label="Completed" value={safeFormat(job.updated_at, "MMM d, yyyy")} variant="muted" />
                      </JobCardInfoSection>
                      <JobCardActions>
                        <Button
                          onClick={() => { setJobMessagesInboxOpen(job.id); setOpenedJobMessageIds(prev => { const n = new Set(prev); n.add(job.id); return n; }); setJobs(prev => prev.map(j => j.id === job.id ? { ...j, unread_message_count: 0 } : j)); }}
                          variant="outline" size="sm"
                          className={`w-full shadow-sm hover:shadow-md transition-all duration-200 text-sm ${
                            getUnread(job.id) > 0 ? 'border-destructive bg-destructive/10 text-destructive animate-pulse' : ''
                          }`}
                        >
                          <MessageCircle className="h-4 w-4 mr-1.5" /> Job Messages ({getUnread(job.id)})
                        </Button>
                        <JobCardButton onClick={() => handleArchiveJob(job)} icon={<Archive className="h-4 w-4" />} label="Archive Job" />
                      </JobCardActions>
                      {/* Rating Section */}
                      {currentUserProfile && (
                        <div>
                          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mb-3"></div>
                          <JobRatingSection job={job} currentUserProfile={currentUserProfile} onJobArchived={fetchJobs} />
                        </div>
                      )}
                    </JobCardBody>
                  </JobCardShell>
                ))}
              </div>
            </TabsContent>
          </Tabs>}
      </main>

      {/* Contact Information Footer */}
      <footer className="bg-muted/30 border-t border-border/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <div className="bg-primary/10 p-2 rounded-full">
                <Phone className="h-4 w-4 text-primary" />
              </div>
              <span className="font-medium">0800 043 8880</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <div className="bg-accent-orange/10 p-2 rounded-full">
                <Mail className="h-4 w-4 text-accent-orange" />
              </div>
              <span className="font-medium">realise@post.com</span>
            </div>
            <Link to="/how-it-works-customer" className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium">
              <FileCheck className="h-4 w-4" />
              How It Works
            </Link>
            <LegalDocumentDownloader />
          </div>
        </div>
      </footer>

      {/* Live Chat Widget */}
      <LiveChatWidget />

      {(showLocationControls && (trackingJob?.id || jobs.some(job => job.status === 'assigned' || job.status === 'in_progress'))) && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm w-[90vw]">
          <LocationControls 
            userType="customer" 
            activeJobId={(trackingJob?.id) ?? (jobs.find(job => job.status === 'assigned' || job.status === 'in_progress')?.id)}
            onClose={() => setShowLocationControls(false)}
          />
        </div>
      )}

      {/* Email Verification Dialog */}
      {showEmailVerificationDialog && currentUserProfile && (
        <EmailVerificationDialog
          isOpen={showEmailVerificationDialog}
          onClose={() => setShowEmailVerificationDialog(false)}
          userEmail={currentUserProfile.email}
          isVerified={currentUserProfile.email_verified}
          onVerificationUpdate={handleVerificationUpdate}
        />
      )}

      {/* Contact Verification Dialog */}
      {showVerificationDialog && currentUserProfile && <ContactVerificationDialog isOpen={showVerificationDialog} onClose={() => setShowVerificationDialog(false)} profile={currentUserProfile} onVerificationComplete={async (verificationType, isReset = false) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: latest } = await supabase
            .from("profiles")
            .select("email_verified, phone_verified")
            .eq("user_id", user.id)
            .single();

          if (latest) {
            setCurrentUserProfile(prev => prev ? { ...prev, ...latest } : prev);
          }
        }
      } catch (e) {
        console.error('Failed to refresh verification status:', e);
      }

      // Close verification dialog
      setShowVerificationDialog(false);

      // Show status message
      const action = isReset ? 'reset' : 'verified';
      toast({
        title: isReset ? "Verification Reset" : "Verification Complete",
        description: `Your ${verificationType} has been ${action} successfully.`
      });
    }} />}

{showCreateJob && (
  <CreateJobDialog open={showCreateJob} onOpenChange={setShowCreateJob} onJobCreated={fetchJobs} />
)}

      <JobTrackingDialog job={trackingJob} onClose={() => setTrackingJob(null)} />

      <TradesmanSelectionDialog open={!!selectingTradesmanForJob} onOpenChange={open => !open && setSelectingTradesmanForJob(null)} jobId={selectingTradesmanForJob?.id || ""} jobCategory={selectingTradesmanForJob?.job_category} onTradesmanSelected={fetchJobs} />

      <SimpleJobResponsesDialog open={viewingResponsesForJob !== ""} onOpenChange={open => {
      if (!open) setViewingResponsesForJob("");
    }} jobId={viewingResponsesForJob} onJobUpdated={fetchJobs} onOpenChat={(jobId, tradesmanId) => {
      // Find the job and create a full job object for messaging
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        setMessagingJob(job);
        setMessagingRecipientId(tradesmanId);
      }
    }} />

      <JobMessagesInbox
        open={jobMessagesInboxOpen !== ""}
        onOpenChange={(open) => {
          if (open) {
            // Optimistically clear unread count and suppress blinking for the opened job
            setOpenedJobMessageIds(prev => { const n = new Set(prev); if (jobMessagesInboxOpen) n.add(jobMessagesInboxOpen); return n; });
            setJobs(prev => prev.map(j => j.id === jobMessagesInboxOpen ? { ...j, unread_message_count: 0 } : j));
          } else {
            // Just close; keep suppression to prevent blinking from returning
            setJobMessagesInboxOpen("");
          }
        }}
        jobId={jobMessagesInboxOpen}
        jobTitle={jobs.find(j => j.id === jobMessagesInboxOpen)?.title || ""}
        onOpenChat={(jobId, tradesmanId) => {
          const job = jobs.find(j => j.id === jobId);
          if (job) {
            setMessagingJob(job);
            setMessagingRecipientId(tradesmanId);
          }
        }}
      />

      <PaymentDialog job={paymentJob} open={!!paymentJob && !!paymentJob.agreed_cost} onClose={() => setPaymentJob(null)} onPaymentSuccess={() => {
      setPaymentJob(null);
      fetchJobs();
    }} />

      <JobMessagingDialog job={messagingJob} onClose={() => {
      console.log('Closing messaging dialog');
      setMessagingJob(null);
      setMessagingRecipientId(undefined);
      // Refresh jobs to update unread message counts
      fetchJobs();
    }} recipientId={messagingRecipientId} />

      <JobPhotoManager jobId={managingPhotosJobId} open={!!managingPhotosJobId} onOpenChange={open => !open && setManagingPhotosJobId("")} isCustomer={true} />

      <JobArchiveDialog isOpen={isArchiveDialogOpen} onClose={() => {
      setIsArchiveDialogOpen(false);
      setSelectedJobForAction(null);
    }} jobId={selectedJobForAction?.id || ""} jobTitle={selectedJobForAction?.title || ""} onArchived={handleArchiveSuccess} />

      <JobCancelDialog isOpen={isCancelDialogOpen} onClose={() => {
      setIsCancelDialogOpen(false);
      setSelectedJobForAction(null);
    }} jobId={selectedJobForAction?.id || ""} jobTitle={selectedJobForAction?.title || ""} onCancelled={handleCancelSuccess} />

      <ArchivedJobsDialog isOpen={showArchivedJobs} onClose={() => setShowArchivedJobs(false)} onJobRestored={(restoredJob: any) => {
        // Optimistically remove archive filter and surface the job immediately
        setArchivedJobIds((prev) => prev.filter((id) => id !== restoredJob.id));
        setJobs((prev) => {
          const exists = prev.some((j) => j.id === restoredJob.id);
          if (exists) {
            return prev.map((j) => (j.id === restoredJob.id ? { ...j, ...restoredJob } : j));
          }
          const enriched = {
            ...restoredJob,
            response_count: 0,
            message_count: 0,
            unread_message_count: 0,
            total_activity_count: 0,
          };
          return [enriched as any, ...prev];
        });
        // Mark as recently restored to avoid flicker during background sync
        restoredRecentlyRef.current.set(restoredJob.id, Date.now());
        // Persistently pin this completed job so it stays visible until you archive it again
        setPinnedCompletedJobIds(prev => {
          const next = new Set(prev);
          next.add(restoredJob.id);
          return next;
        });
        // Force show this job for 60s regardless of archived state
        setForceShowJobIds(prev => {
          const next = new Set(prev);
          next.add(restoredJob.id);
          return next;
        });
        setTimeout(() => {
          setForceShowJobIds(prev => {
            const next = new Set(prev);
            next.delete(restoredJob.id);
            return next;
          });
        }, 60000);
        // Switch to Completed tab and close the dialog for immediate visibility
        setActiveTab('completed');
        setShowArchivedJobs(false);
        // Background sync to ensure counts/photos are up to date
        fetchArchivedJobIds();
        fetchJobs();
      }} />

      <ContactInfoDialog open={showContactInfo} onOpenChange={setShowContactInfo} currentProfile={currentUserProfile} onProfileUpdate={fetchJobs} />

      {/* User Ratings Dialog */}
      {showUserRatingsDialog && currentUserProfile && <UserRatingsDialog isOpen={showUserRatingsDialog} onClose={() => setShowUserRatingsDialog(false)} currentUserProfile={currentUserProfile} />}

      {/* Customer Reviews Dialog */}
      <CustomerReviewsDialog
        customerId={currentUserProfile?.id || null}
        open={showReviewsDialog}
        onOpenChange={setShowReviewsDialog}
      />

      {/* Report Job Issue Dialog */}
      <ReportJobIssueDialog job={reportingJob} onClose={() => setReportingJob(null)} />

      {/* Customer Job Detail Dialog */}
      <CustomerJobDetailDialog job={customerJobDetailDialog} onClose={() => setCustomerJobDetailDialog(null)} onStartChat={(jobId, tradesmanId) => {
      setMessagingJob(customerJobDetailDialog);
      setMessagingRecipientId(tradesmanId);
      setCustomerJobDetailDialog(null);
    }} />
      
      {/* Edit Job Dialog */}
      <EditJobDialog
        open={!!editingJob}
        onOpenChange={(open) => !open && setEditingJob(null)}
        job={editingJob}
        onJobUpdated={() => {
          fetchJobs();
          setEditingJob(null);
        }}
      />
      
      {/* Password Reset Dialog */}
      <PasswordResetDialog
        open={showPasswordReset}
        onOpenChange={onPasswordResetChange}
      />
      
      {/* Floating App Download Widget */}
      <FloatingAppDownload />
    </div>;
};